import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { onAuthStateChanged, signOut as fbSignOut } from "firebase/auth";
import { getDoc } from "firebase/firestore";
import { auth, WEBSITE_URL } from "@lib/firebase";
import { businessUserRef } from "@/services/firestorePaths";

const AuthContext = createContext(null);

/**
 * Resolves which business the signed-in user belongs to.
 *
 * businessId is "Bus-" + uid by convention - the Traka POS app builds it that
 * way and the deployed Firestore rules require it (isBusinessOwner checks
 * businessId == 'Bus-' + request.auth.uid). So the common case is derivable
 * with no query at all: one direct document read confirms membership, needs no
 * index, and works without the website being reachable.
 *
 * The claim set by the handoff is checked first because it costs nothing, and
 * the server is the last resort - only it can find a legacy business created by
 * the old website signup, which used a random id (that needs an admin-SDK
 * collection-group query a client can never be granted).
 */
async function resolveBusinessId(user) {
  const { claims } = await user.getIdTokenResult();
  if (claims.businessId) {
    return { businessId: claims.businessId, role: claims.role || "Staff" };
  }

  const conventionalId = `Bus-${user.uid}`;
  try {
    const snap = await getDoc(businessUserRef(conventionalId, user.uid));
    if (snap.exists()) {
      const data = snap.data() || {};
      return {
        businessId: data.businessId || conventionalId,
        role: data.role || "Staff",
      };
    }
  } catch (err) {
    // permission-denied just means this is not the owner's own business; the
    // server can still resolve it, so fall through rather than fail here.
  }

  const idToken = await user.getIdToken();
  const res = await fetch(`${WEBSITE_URL}/api/auth/dashboard-handoff`, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}` },
  }).catch(() => null);

  if (!res || !res.ok) {
    const reason = !res ?
      `Could not reach ${WEBSITE_URL} to look up your business.` :
      (await res.json().catch(() => ({}))).error || "Business lookup failed.";
    const error = new Error(reason);
    error.reason = reason;
    throw error;
  }

  const body = await res.json();
  // The claim now exists server-side; refresh so Firestore rules and any later
  // read see it without waiting for the hourly token rotation.
  await user.getIdToken(true);

  return { businessId: body.businessId || null, role: body.role || "Staff" };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Cancels the in-flight resolve if auth changes again before it settles,
    // so a stale businessId can never land on a newer user.
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      if (!nextUser) {
        if (!active) return;
        setUser(null);
        setBusinessId(null);
        setRole(null);
        setError(null);
        setLoading(false);
        return;
      }

      setUser(nextUser);
      try {
        const resolved = await resolveBusinessId(nextUser);
        if (!active) return;
        setBusinessId(resolved.businessId);
        setRole(resolved.role);
        setError(
          resolved.businessId ?
            null :
            `No business found at Bus-${nextUser.uid}. This account may not have completed setup.`
        );
      } catch (err) {
        if (!active) return;
        setError(err.reason || err.message || "Could not load your business.");
      } finally {
        if (active) setLoading(false);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    window.location.href = `${WEBSITE_URL}/login`;
  }, []);

  const value = useMemo(
    () => ({
      user,
      businessId,
      role,
      loading,
      error,
      signOut,
      isAuthenticated: Boolean(user && businessId),
    }),
    [user, businessId, role, loading, error, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
  return ctx;
}

export default AuthContext;
